import { Panel, PanelHeader } from './panel';

type LockedOverlayProps = {
  title: string;
  message: string;
  onUnlockClick: () => void;
};

/**
 * Sits over a module whose data is withheld from free visitors. The content
 * beneath must already be blank or placeholder — this is a visual affordance,
 * not the gate. Real values are never sent to the client for a locked module.
 */
export const LockedOverlay = ({
  title,
  message,
  onUnlockClick
}: LockedOverlayProps) => (
  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/70 via-white/90 to-white px-5 text-center backdrop-blur-[2px]">
    <div className="max-w-sm">
      <span aria-hidden className="text-2xl">
        🔒
      </span>
      <p className="mt-2 font-sans text-base font-bold text-neutral-800">
        {title}
      </p>
      <p className="mt-1.5 text-[13px] text-neutral-700">{message}</p>
      <button
        type="button"
        onClick={onUnlockClick}
        className="mt-4 inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      >
        Unlock with membership
      </button>
    </div>
  </div>
);

/** Minimum height that keeps the overlay's copy and button comfortably clear. */
const LOCKED_PANEL_MIN_HEIGHT = 'min-h-[220px]';

type LockedModulePanelProps = {
  title: string;
  subtitle?: string;
  message: string;
  onUnlockClick: () => void;
};

/**
 * A whole module withheld from free visitors. The queries behind it are
 * disabled upstream, so this renders in place of the data rather than over it —
 * nothing withheld is ever sent to the client.
 */
export const LockedModulePanel = ({
  title,
  subtitle,
  message,
  onUnlockClick
}: LockedModulePanelProps) => (
  <Panel className="overflow-hidden">
    <PanelHeader title={title} subtitle={subtitle} />
    <div
      className={`relative border-t border-neutral-200 ${LOCKED_PANEL_MIN_HEIGHT}`}
    >
      <LockedOverlay
        title={`${title} is a member feature`}
        message={message}
        onUnlockClick={onUnlockClick}
      />
    </div>
  </Panel>
);
